import { useEffect, useState, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Loader2,
  Share2,
  Check,
  AlertTriangle,
} from "lucide-react";

/**
 * PushPointPlayer - External m3u8 stream player with fallback video
 * Used for "Push Point Mode" where channels receive external streams
 */
export const PushPointPlayer = ({
  streamUrl,        // External m3u8 URL
  fallbackUrl,      // Fallback video URL when stream is offline
  isLive = true,
  autoPlay = true,
  onReady,
  onStreamStatus,   // Callback for stream status changes
}) => {
  const videoRef = useRef(null);
  const fallbackRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsTimeout = useRef(null);
  const retryTimeout = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Start muted for autoplay compliance
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showShareToast, setShowShareToast] = useState(false);
  
  // Stream status
  const [streamOnline, setStreamOnline] = useState(false);
  const [showingFallback, setShowingFallback] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_INTERVAL = 10000; // 10 seconds

  // Check if stream is available
  const checkStreamAvailability = useCallback(async () => {
    if (!streamUrl) {
      setStreamOnline(false);
      setShowingFallback(true);
      return false;
    }

    try {
      // Try to fetch the m3u8 manifest
      const response = await fetch(streamUrl, { 
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        const text = await response.text();
        // Check if it's a valid HLS manifest
        if (text.includes('#EXTM3U')) {
          return true;
        }
      }
      return false;
    } catch (err) {
      console.log("Stream check failed:", err.message);
      return false;
    }
  }, [streamUrl]);

  // Initialize HLS player for live stream
  const initHlsStream = useCallback(async () => {
    if (!streamUrl || !videoRef.current) return;

    try {
      const Hls = (await import("hls.js")).default;

      if (Hls.isSupported()) {
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }

        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 30,
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 2,
          levelLoadingTimeOut: 10000,
          fragLoadingTimeOut: 10000,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
        });

        hlsRef.current = hls;

        hls.loadSource(streamUrl);
        hls.attachMedia(videoRef.current);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setIsLoading(false);
          setStreamOnline(true);
          setShowingFallback(false);
          setRetryCount(0);
          onStreamStatus?.("online");
          
          if (autoPlay) {
            videoRef.current.play().catch(() => {});
          }
          onReady?.();
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          console.log("HLS error:", data.type, data.details);
          
          if (data.fatal) {
            console.log("Fatal HLS error, switching to fallback");
            hls.destroy();
            hlsRef.current = null;
            
            // Switch to fallback
            setStreamOnline(false);
            setShowingFallback(true);
            setIsLoading(false);
            onStreamStatus?.("offline");
            
            // Schedule retry
            if (retryCount < MAX_RETRIES) {
              retryTimeout.current = setTimeout(() => {
                setRetryCount(prev => prev + 1);
                initHlsStream();
              }, RETRY_INTERVAL);
            }
          }
        });

      } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS
        videoRef.current.src = streamUrl;
        videoRef.current.addEventListener("loadedmetadata", () => {
          setIsLoading(false);
          setStreamOnline(true);
          setShowingFallback(false);
          onStreamStatus?.("online");
          
          if (autoPlay) {
            videoRef.current.play().catch(() => {});
          }
          onReady?.();
        });
        
        videoRef.current.addEventListener("error", () => {
          setStreamOnline(false);
          setShowingFallback(true);
          setIsLoading(false);
          onStreamStatus?.("offline");
        });
      }
    } catch (e) {
      console.error("HLS init error:", e);
      setStreamOnline(false);
      setShowingFallback(true);
      setIsLoading(false);
      onStreamStatus?.("offline");
    }
  }, [streamUrl, autoPlay, onReady, onStreamStatus, retryCount]);

  // Handle fallback video playback when showing fallback
  useEffect(() => {
    if (showingFallback && fallbackRef.current && fallbackUrl) {
      // Ensure fallback video plays when we switch to fallback mode
      const playFallback = async () => {
        try {
          fallbackRef.current.load();
          await fallbackRef.current.play();
          setIsPlaying(true);
        } catch (err) {
          console.log("Fallback video autoplay failed:", err.message);
        }
      };
      playFallback();
    }
  }, [showingFallback, fallbackUrl]);

  // Initial stream check and setup
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const isAvailable = await checkStreamAvailability();
      
      if (isAvailable) {
        initHlsStream();
      } else {
        setStreamOnline(false);
        setShowingFallback(true);
        setIsLoading(false);
        onStreamStatus?.("offline");
        
        // Start fallback video
        if (fallbackRef.current && fallbackUrl) {
          fallbackRef.current.play().catch(() => {});
        }
        
        // Schedule retry
        retryTimeout.current = setTimeout(() => {
          setRetryCount(prev => prev + 1);
          init();
        }, RETRY_INTERVAL);
      }
    };

    init();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
    };
  }, [streamUrl, fallbackUrl, checkStreamAvailability, initHlsStream, onStreamStatus]);

  // Video event handlers
  useEffect(() => {
    const video = streamOnline ? videoRef.current : fallbackRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, [streamOnline]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (isPlaying) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleMouseMove = () => resetControlsTimeout();
  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  // Controls
  const togglePlay = () => {
    const video = streamOnline ? videoRef.current : fallbackRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
    }
  };

  const toggleMute = () => {
    const video = streamOnline ? videoRef.current : fallbackRef.current;
    if (video) {
      video.muted = !isMuted;
    }
  };

  const handleVolumeSliderChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    const video = streamOnline ? videoRef.current : fallbackRef.current;
    if (video) {
      video.volume = newVolume;
      video.muted = newVolume === 0;
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black group"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="pushpoint-player"
    >
      {/* Live Stream Video (hidden when showing fallback) */}
      <video
        ref={videoRef}
        className={`w-full h-full ${showingFallback ? 'hidden' : 'block'}`}
        playsInline
        onClick={togglePlay}
      />

      {/* Fallback Video (looping) */}
      {fallbackUrl && (
        <video
          ref={fallbackRef}
          className={`w-full h-full object-contain ${showingFallback ? 'block' : 'hidden'}`}
          style={{ backgroundColor: '#000' }}
          playsInline
          onClick={togglePlay}
          loop
          muted
          autoPlay
          preload="auto"
          onLoadedData={() => {
            // Try to play as soon as data is loaded
            if (showingFallback && fallbackRef.current) {
              fallbackRef.current.play().catch(err => {
                console.log("Fallback autoplay blocked:", err.message);
              });
            }
          }}
          onError={(e) => console.log("Fallback video error:", e.target.error)}
        >
          <source src={fallbackUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Live Badge */}
      {streamOnline && isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest z-20">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
      )}

      {/* Offline/Fallback Badge */}
      {showingFallback && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-yellow-600 text-black text-xs font-black uppercase tracking-widest z-20">
          <AlertTriangle className="w-4 h-4" />
          STREAM OFFLINE
        </div>
      )}

      {/* Retry Notice - Hidden per user request */}

      {/* Share Button */}
      <button
        onClick={handleShare}
        className={`absolute top-4 ${streamOnline ? 'left-24' : 'left-48'} flex items-center gap-2 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-bold uppercase tracking-wider z-20 transition-colors`}
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>

      {/* Share Toast */}
      {showShareToast && (
        <div className="absolute top-16 left-4 flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-xs font-bold z-30">
          <Check className="w-4 h-4" />
          Link copied!
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Center Play Button */}
        <button
          className="absolute inset-0 flex items-center justify-center"
          onClick={togglePlay}
        >
          {!isPlaying && (
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
          )}
        </button>

        {/* Bottom Controls Bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="flex items-center justify-between gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white" fill="white" />
                )}
              </button>

              {/* Volume */}
              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={toggleMute}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeSliderChange}
                  className="w-0 group-hover/volume:w-20 transition-all duration-200 accent-red-600"
                />
              </div>

              {/* Stream Status - Hidden per user request */}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5 text-white" />
                ) : (
                  <Maximize className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushPointPlayer;
